import { FormEvent, useState } from "react";
import { LoaderCircle } from "lucide-react";

import {
  createScheduleBlock,
  createTimeOff,
  updateScheduleBlock,
  updateTimeOff,
} from "./availability-api";
import { isoToBusinessLocal, localBusinessTimeToIso } from "./availability-time";
import type { ScopedAvailability, WorkforceOverview } from "./types";

export function AvailabilityPeriodForm({
  kind,
  record,
  workforce,
  canWrite,
  onClose,
  onSaved,
}: {
  kind: "timeOff" | "scheduleBlocks";
  record?: ScopedAvailability;
  workforce: WorkforceOverview;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const target = String(fields.get("target"));
    const [targetKind, targetId] = target.split(":", 2);
    const input = {
      employeeId: targetKind === "employee" ? targetId : null,
      teamId: targetKind === "team" ? targetId : null,
      startAt: localBusinessTimeToIso(String(fields.get("startAt"))),
      endAt: localBusinessTimeToIso(String(fields.get("endAt"))),
      reason: String(fields.get("reason")),
      notes: nullable(fields.get("notes")),
      active: fields.get("active") === "on",
    };
    try {
      if (kind === "timeOff") {
        await (record ? updateTimeOff(record.id, input) : createTimeOff(input));
      } else {
        await (record ? updateScheduleBlock(record.id, input) : createScheduleBlock(input));
      }
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  const target = record?.employeeId
    ? `employee:${record.employeeId}`
    : record?.teamId
      ? `team:${record.teamId}`
      : "";
  return (
    <form className="record-form availability-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="wide-field">
          Applies to
          <select name="target" defaultValue={target} required>
            <option value="" disabled>
              Select an employee or team
            </option>
            <optgroup label="Employees">
              {workforce.employees.map((employee) => (
                <option key={employee.id} value={`employee:${employee.id}`}>
                  {employee.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Teams">
              {workforce.teams.map((team) => (
                <option key={team.id} value={`team:${team.id}`}>
                  {team.name}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
        <label>
          Starts · Massachusetts time
          <input
            name="startAt"
            type="datetime-local"
            required
            defaultValue={isoToBusinessLocal(record?.startAt)}
          />
        </label>
        <label>
          Ends · Massachusetts time
          <input
            name="endAt"
            type="datetime-local"
            required
            defaultValue={isoToBusinessLocal(record?.endAt)}
          />
        </label>
        <label className="wide-field">
          Reason
          <input name="reason" required maxLength={240} defaultValue={record?.reason} />
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" rows={4} maxLength={10000} defaultValue={record?.notes ?? ""} />
        </label>
        <label className="toggle-field workforce-toggle">
          <input name="active" type="checkbox" defaultChecked={record?.active ?? true} /> Active
          scheduling exception
        </label>
      </div>
      <p className="form-guidance">
        Times are interpreted in the TLC Massachusetts business timezone, independent of this
        device’s location.
      </p>
      {failed ? (
        <p className="form-error" role="alert">
          We couldn’t save this period. Check the target and that the end is after the start.
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending || !canWrite}>
          {sending ? <LoaderCircle className="spin" /> : null} Save period
        </button>
      </div>
    </form>
  );
}

function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
